<script setup lang="ts">
  import {
    Cancel,
    FlowTypes,
    Info,
    Journey,
    Logout,
    Pause,
    Swap,
    Truck,
    LicensePlate,
    Cross,
    UiButton,
    type FlowConfig,
    Reorder,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, type Component } from 'vue'
  import Logo from '@/components/Logo.vue'
  import { useFlowConfigStore } from '@/store/flow-config.store'

  const flows = defineModel<FlowConfig[]>('flows', { required: true })
  const { homeConfig, menuConfig, colours } = defineProps<{
    homeConfig: { widget: string }[]
    menuConfig: { widget: string }[]
    activeTab: string
    colours: { css: string; value: string }[]
  }>()

  const flowConfigStore = useFlowConfigStore()
  const toastStore = useToastStore()
  const emit = defineEmits<{
    openMenu: []
    closeMenu: []
  }>()

  function splitCamelCase(string: string) {
    return string.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, word => word.toUpperCase())
  }

  const menuIconMap: Record<string, Component> = {
    licensePlate: Truck,
    licensePlateBGB: Truck,
    queue: Pause,
    activeShipments: Journey,
    historyShipments: History,
    activeBgbShipments: Journey,
    historyBgbShipments: History,
    cancelShipment: Cancel,
    switchEmployer: Swap,
    disclaimer: Info,
    signOut: Logout,
  }

  const orderableFlows = computed(() =>
    flows.value
      .filter(flow => flow.type !== FlowTypes.registration)
      .sort((one, two) => one.order - two.order)
  )

  async function saveOrder(updatedFlows: FlowConfig[]) {
    try {
      await Promise.all(updatedFlows.map(flow => flowConfigStore.updateFlow(flow)))
      toastStore.success('De volgorde van de flows is succesvol opgeslagen')
    } catch (error) {
      toastStore.error('Er is een fout opgetreden bij het opslaan van de flow volgorde')
      throw error
    }
  }

  function handleReorder(value: FlowConfig[]) {
    const reindexed = value.map((flow, index) => ({ ...flow, order: index + 1 }))

    const registrationFlows = flows.value.filter(flow => flow.type === FlowTypes.registration)
    flows.value = [...registrationFlows, ...reindexed]

    saveOrder(reindexed)
  }
</script>

<template>
  <div class="preview-container">
    <div
      class="status-bar"
      :style="{
        backgroundColor: colours.find(colour => colour.css === '--colours-status-bar')?.value,
      }"
    />
    <div
      class="banner"
      :style="{
        backgroundColor: colours.find(colour => colour.css === '--colours-app-bar')?.value,
        '--colours-app-bar-text': colours.find(colour => colour.css === '--colours-app-bar-text')
          ?.value,
      }"
    >
      <Logo class="logo" />

      <div class="menu-button">
        <UiButton label="Menu" @click="emit('openMenu')" size="small" />
      </div>
    </div>

    <div class="preview">
      <div class="preview-form">
        <div v-if="homeConfig.length > 0 || activeTab === 'style'" class="home-config">
          <div
            v-for="config in homeConfig.filter(config => config.widget !== 'fetch')"
            :key="config.widget"
          >
            <div>
              <LicensePlate v-if="config.widget === 'licensePlate'" />
            </div>

            <div v-if="config.widget === 'actions'" class="button-container">
              <template v-for="(flow, index) in orderableFlows" :key="flow.id">
                <div class="button-preview-container">
                  <UiButton
                    :label="flow.label"
                    class="button-preview"
                    :activityIndicator="flow.activityIndicatorPath ? 3 : null"
                    :activityIndicatorColour="flow.activityIndicatorColour"
                    :activityIndicatorTextColour="flow.activityIndicatorTextColour"
                    :style="{
                      backgroundColor:
                        flow.buttonColour ||
                        colours.find(colour => colour.css === '--colours-primary')?.value ||
                        'var(--colours-primary, #ffffff)',
                      color:
                        flow.textColour ||
                        colours.find(colour => colour.css === '--colours-primary-text')?.value ||
                        'var(--colours-primary-text, #000000)',
                    }"
                    icon="chevronRight"
                  />
                  <Reorder
                    :modelValue="orderableFlows"
                    :index="index"
                    @update:modelValue="handleReorder"
                  />
                </div>
              </template>
            </div>

            <UiButton
              v-if="config.widget === 'activeShipments'"
              label="Actieve Ritten"
              class="button-preview"
              :style="{
                backgroundColor:
                  colours.find(colour => colour.css === '--colours-secondary')?.value ||
                  'var(--colours-secondary, #ffffff)',
                color:
                  colours.find(colour => colour.css === '--colours-secondary-text')?.value ||
                  'var(--colours-secondary-text, #000000)',
              }"
              icon="chevronRight"
            />

            <UiButton
              v-if="config.widget === 'historyShipments'"
              label="Rit Geschiedenis"
              class="button-preview"
              :style="{
                backgroundColor:
                  colours.find(colour => colour.css === '--colours-secondary')?.value ||
                  'var(--colours-secondary, #ffffff)',
                color:
                  colours.find(colour => colour.css === '--colours-secondary-text')?.value ||
                  'var(--colours-secondary-text, #000000)',
              }"
              icon="chevronRight"
            />

            <UiButton
              v-if="config.widget === 'activeBgbShipments'"
              label="Actieve BGB Ritten"
              class="button-preview"
              :style="{
                backgroundColor:
                  colours.find(colour => colour.css === '--colours-secondary')?.value ||
                  'var(--colours-secondary, #ffffff)',
                color:
                  colours.find(colour => colour.css === '--colours-secondary-text')?.value ||
                  'var(--colours-secondary-text, #000000)',
              }"
              icon="chevronRight"
            />

            <UiButton
              v-if="config.widget === 'historyBgbShipments'"
              label="Rit BGB Geschiedenis"
              class="button-preview"
              :style="{
                backgroundColor:
                  colours.find(colour => colour.css === '--colours-secondary')?.value ||
                  'var(--colours-secondary, #ffffff)',
                color:
                  colours.find(colour => colour.css === '--colours-secondary-text')?.value ||
                  'var(--colours-secondary-text, #000000)',
              }"
              icon="chevronRight"
            />
          </div>
        </div>
      </div>
    </div>

    <Transition name="menu-fade">
      <div v-if="activeTab === 'menu'" class="menu-backdrop" @click="emit('closeMenu')" />
    </Transition>

    <Transition name="menu-slide">
      <div v-if="activeTab === 'menu'" class="menu-panel">
        <div
          class="banner menu-panel-banner"
          :style="{
            backgroundColor: colours.find(colour => colour.css === '--colours-menu-bar')?.value,
            '--colours-menu-bar-text': colours.find(
              colour => colour.css === '--colours-menu-bar-text'
            )?.value,
          }"
        >
          <Logo class="logo" />
          <div class="menu-close-button">
            <Cross @click="emit('closeMenu')" class="close-btn" size="large" />
          </div>
        </div>

        <ul class="menu-list-preview">
          <li v-for="menuItem in menuConfig" :key="menuItem.widget">
            <div class="menu-item-preview">
              <span class="menu-item-label">{{ splitCamelCase(menuItem.widget) }}</span>
              <component
                v-if="menuIconMap[menuItem.widget]"
                :is="menuIconMap[menuItem.widget]"
                class="menu-item-icon-preview"
                size="small"
              />
            </div>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="css">
  .button-preview-container {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);
  }

  .menu-button {
    position: absolute;
    right: 1rem;
  }

  .menu-button :deep(.button.default) {
    border: 1px solid var(--colours-app-bar-text, var(--colours-grey-1));
    border-radius: 0.25rem;
    padding: 0.5rem;
    color: var(--colours-app-bar-text, var(--colours-grey-1));
    background-color: transparent;
    text-transform: uppercase;
  }

  .preview-container {
    width: 20rem;
    height: 35rem;
    background-color: var(--colours-grey-1);
    border: 1px solid var(--colours-grey-3);
    box-shadow: 1px 1px 1px 1px var(--colours-grey-3);
    border-radius: 0.625rem;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  .logo {
    max-height: var(--spacing-l);
    padding: var(--spacing-xs);
  }

  .status-bar {
    flex-shrink: 0;
    height: var(--spacing-s);
    background: var(--colours-status-bar);
    border-radius: var(--spacing-xs) var(--spacing-xs) 0 0;
  }

  .banner {
    background: var(--colours-brand-colour-1);
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(var(--spacing-l) + env(safe-area-inset-top));
  }

  .preview-form {
    scrollbar-width: none;
    padding: var(--spacing-s);
    width: 100%;
    min-width: 100%;
    height: 95%;
    min-height: 90%;
    overflow: auto;
  }

  .preview {
    flex: 1;
    width: 100%;
    overflow-y: hidden;
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .button-preview {
    width: 100%;
    justify-content: space-between;
  }

  .home-config {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .menu-list-preview {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  .menu-item-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--colours-grey-3);
    color: var(--colours-grey-5);
    cursor: pointer;
  }

  .menu-item-icon-preview {
    flex-shrink: 0;
    color: var(--colours-grey-5);
  }

  .menu-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
  }

  .menu-fade-enter-active,
  .menu-fade-leave-active {
    transition: opacity 0.25s ease;
  }

  .menu-fade-enter-from,
  .menu-fade-leave-to {
    opacity: 0;
  }

  .menu-panel {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 80%;
    max-width: 320px;
    background: var(--colours-grey-1);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .menu-panel-banner {
    border-radius: 0;
    flex-shrink: 0;
    position: relative;
  }

  .menu-close-button {
    position: absolute;
    right: var(--spacing-s);
  }

  .menu-slide-enter-active,
  .menu-slide-leave-active {
    transition: transform 0.3s ease;
  }

  .menu-slide-enter-from,
  .menu-slide-leave-to {
    transform: translateX(100%);
  }

  .menu-slide-enter-to,
  .menu-slide-leave-from {
    transform: translateX(0);
  }

  .menu-list-preview {
    list-style: none;
    margin: 0;
    padding: var(--spacing-s);
    flex: 1;
    overflow-y: auto;
  }

  .menu-item-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--colours-grey-3);
    color: var(--colours-grey-5);
    cursor: pointer;
  }

  .menu-item-icon-preview {
    flex-shrink: 0;
    color: var(--colours-grey-5);
  }

  .close-btn {
    cursor: pointer;
    height: var(--spacing-m);
    width: var(--spacing-m);
    color: var(--colours-menu-bar-text, var(--colours-grey-1));
  }
</style>
